import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCart, addToCart, updateCart, removeFromCart, clearCart } from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    try {
      setCartLoading(true);
      const { data } = await getCart();
      setCart(data);
    } catch (err) {
      console.log('Cart error:', err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart([]);
    }
  }, [user, fetchCart]);

  const addItem = async (productId, quantity = 1) => {
    try {
      const { data } = await addToCart({ productId, quantity });
      setCart(data);
    } catch (err) {
      console.log('Add to cart error:', err);
    }
  };

  const updateItem = async (productId, quantity) => {
    try {
      const { data } = await updateCart(productId, { quantity });
      setCart(data);
    } catch (err) {
      console.log('Update cart error:', err);
    }
  };

  const removeItem = async (productId) => {
    try {
      await removeFromCart(productId);
      setCart(cart.filter(item => item.product._id !== productId));
    } catch (err) {
      console.log('Remove from cart error:', err);
    }
  };

  const emptyCart = async () => {
    try {
      await clearCart();
      setCart([]);
    } catch (err) {
      console.log('Clear cart error:', err);
    }
  };

  const cartTotal = cart.reduce((total, item) => {
    const price = item.product?.discountedPrice || item.product?.price || 0;
    return total + price * item.quantity;
  }, 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      cartLoading,
      cartTotal,
      cartCount,
      addItem,
      updateItem,
      removeItem,
      emptyCart,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

export default CartContext;